<script setup lang="ts">
  import { Loader2 } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, onBeforeUnmount, inject, ref, type Ref } from 'vue'
  import Progress from '@/components/ui/progress/progress.vue'
  import PdfDisplay from '@/components/pdf/PdfDisplay.vue'
  import SignModal from '@/components/pdf/SignModalV3.vue'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  import { downloadDocument } from '@/lib/utils'
  import { usePDF } from '@/store/pdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import Button from '@/components/ui/button/Button.vue'
  import Header from '@/components/clientPortal/Header.vue'
  import '@tato30/vue-pdf/style.css'
  import CompletedPdf from '@/components/pdf/completedPdf.vue'
  import client404 from '@/components/404/404.vue'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { useRoute } from 'vue-router'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { type ClientCase, type ClientOrganisation } from '@/types/generated'
  import { type DocumentDownload } from '@/types'
  import { useTranslation } from '@/composables/useTranslation'
  import ConfirmationDialog from '@/components/ui/confirmation-dialog/ConfirmationDialog.vue'

  const { t } = useTranslation({
    en: {
      backRequiredActions: 'Back required actions',
      submitDocument: 'Submit Document',
      pageOf: 'of'
    },
    de: {
      backRequiredActions: 'Zurück zu erforderlichen Aktionen',
      submitDocument: 'Dokument abschicken',
      pageOf: 'von'
    }
  })

  const pdf = usePDF()
  const signatureStore = useSignatures()
  const { trackEvent } = useMetrics()
  const themeColor = computed(() => useThemeColor().value)
  const width = ref<number>(window.innerWidth)
  const route = useRoute()

  // Get data from ClientPortalBase
  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const accessToken = inject<Ref<string | null>>('accessToken')
  const org = inject<Ref<ClientOrganisation | null>>('org')

  // Get the specific PDF template from the route params
  const templateToken = route.params.form_token as string
  const pdfTemplate = computed(() => {
    if (!caseRecord?.value?.form_requests) return null
    return (
      caseRecord.value.form_requests.find((request: any) => request.token === templateToken) || null
    )
  })
  const isExternalRoute = computed(() => {
    return !accessToken?.value || route.path.includes('/external/')
  })

  onMounted(async () => {
    signatureStore.setPdfStore('new')
    // Initialize PDF functionality
    pdf.doMount()
    window.addEventListener('resize', () => (width.value = window.innerWidth))

    if (pdfTemplate.value?.form?.id) {
      signatureStore.setManualFormId(pdfTemplate.value.form.id)
    }

    // Track metrics if we have org data
    if (org?.value?.uuid) {
      trackEvent(MetricEventType.CLIENT_PDFDOCUMENT, { organization_id: org.value.uuid })
    }
  })

  onBeforeUnmount(() => {
    signatureStore.cleanup()
  })

  onUnmounted(() => {
    window.removeEventListener('resize', () => (width.value = window.innerWidth))
    pdf.unloadPdf()
    signatureStore.clearSignatures()
  })

  function handleDownloadClick() {
    if (!pdf.selectedForm) {
      return
    }

    const download = pdf.selectedForm.documentProperties.download as DocumentDownload

    downloadDocument(
      download.url,
      pdf.selectedForm.documentProperties.title
        ? pdf.selectedForm.documentProperties.title + '.pdf'
        : download.filename
    )
  }
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Check if there is a selected form -->
    <div v-if="pdf.selectedForm">
      <!-- Check if the document is signed -->
      <CompletedPdf
        v-if="pdf.selectedForm.documentProperties.tag === 'SIGNED'"
        :handleDownloadClick="handleDownloadClick"
        :document-title="
          pdf.selectedForm.documentProperties.title ||
          pdfTemplate?.form?.title ||
          pdfTemplate?.form?.document_name
        "
      />

      <!-- Display the following section if the document is not signed -->
      <div
        v-else
        class="pdf-view-container flex flex-col"
      >
        <Header
          v-if="!isExternalRoute"
          :title="pdf.selectedForm?.documentProperties?.title || 'PDF Document'"
          :show-back-button="!isExternalRoute && !pdfTemplate?.is_third_party"
          :is-external-route="isExternalRoute"
          :full-width="true"
          back-button-data-cy="ClientPortalPDFTemplateDetailViewMobileBackButton"
          title-data-cy="ClientPortalPDFTemplateDetailViewMobileTitle"
        />

        <!-- Main Content Area with top padding for fixed header -->
        <div class="pdf-content-area flex-1 overflow-y-auto pt-24">
          <div class="flex justify-center py-4">
            <div class="pdf-wrapper w-full px-3 md:w-4/5 md:max-w-[869px] md:px-0">
              <!-- PDF Display Area -->
              <div
                v-if="pdf.loadingProgress >= 100"
                class="pdf-container rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                <PdfDisplay :editable="!pdf.waitingForServerResponse" />
                <SignModal v-if="signatureStore.showSignModal" />
              </div>

              <!-- Loading Progress -->
              <div
                v-else
                class="flex h-96 items-center justify-center rounded-xl border border-gray-200 bg-white"
                role="status"
              >
                <Progress
                  :model-value="Math.floor(pdf.loadingProgress)"
                  class="w-3/5"
                  :aria-label="t('submitDocument')"
                />
                <span class="sr-only">Loading PDF document...</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Fixed Bottom Navigation Bar -->
        <div
          v-if="pdf.loadingProgress >= 100"
          class="pdf-bottom-controls border-t border-gray-200 bg-white shadow-lg"
        >
          <div class="flex items-center justify-between p-4">
            <!-- Navigation Controls -->
            <GenericPdfNav
              :current-page="pdf.page"
              :total-pages="pdf.pages"
              :theme-color="themeColor"
              @update-page="pdf.gotoPage"
            />

            <!-- Submit Button -->
            <ConfirmationDialog
              type="document"
              :mobile-bottom-sheet="true"
              :disabled="
                !(
                  pdf.ready &&
                  pdf.hasAllRequired &&
                  !pdf.waitingForServerResponse &&
                  signatureStore.hasAllSignatures
                )
              "
              data-cy="clientResponseViewSendButton"
              @confirm="pdf.submitFormV3"
            >
              <template #trigger>
                <Button
                  :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
                  :disabled="
                    !(
                      pdf.ready &&
                      pdf.hasAllRequired &&
                      !pdf.waitingForServerResponse &&
                      signatureStore.hasAllSignatures
                    )
                  "
                >
                  {{ t('submitDocument') }}
                  <Loader2
                    v-if="pdf.waitingForServerResponse"
                    class="ml-2 h-4 w-4 animate-spin"
                  />
                </Button>
              </template>
            </ConfirmationDialog>
          </div>
        </div>
      </div>
    </div>

    <!-- Display this message if there is no selected form -->
    <div
      v-else
      class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan py-4"
    >
      <div
        v-if="!pdf.selectedForm && pdf.initDone && !pdf.isLoadingPDF && pdf.loadingProgress === 0"
      >
        <client404 />
      </div>
    </div>
    <div id="confirm-modal"></div>
  </div>
</template>

<style scoped>
  input[type='text'],
  textarea {
    border: 1px solid #e5e7eb !important;
    border-radius: 0;
    padding: 0.5rem;
    font-size: 1rem;
    width: 100%;
  }

  /* Desktop layout - standard flow */
  @media (min-width: 768px) {
    .pdf-view-container {
      min-height: 100vh;
    }

    .pdf-content-area {
      padding-bottom: 5rem;
    }

    .pdf-bottom-controls {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 50;
    }
  }

  /* Mobile layout - use viewport units and flexbox */
  @media (max-width: 767px) {
    .pdf-view-container {
      height: 100vh;
      height: 100dvh; /* Use dynamic viewport height for mobile browsers */
      overflow: hidden; /* fallback for iOS Safari < 16 */
      overflow: clip;
    }

    .pdf-content-area {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      /* Add padding for safe area on mobile devices */
      padding-bottom: 1rem;
    }

    .pdf-wrapper {
      max-width: 100vw;
      box-sizing: border-box;
    }

    .pdf-container {
      max-width: 100%;
      overflow: hidden; /* fallback for iOS Safari < 16 */
      overflow: clip;
    }

    /* Constrain PDF to container width on mobile */
    .pdf-container :deep(.pdf) {
      max-width: 100%;
      width: 100%;
    }

    .pdf-container :deep(.textLayer) {
      max-width: 100%;
    }

    .pdf-container :deep(canvas) {
      max-width: 100% !important;
      height: auto !important;
    }

    /* Bottom controls - not fixed on mobile, part of flexbox flow */
    .pdf-bottom-controls {
      flex-shrink: 0;
      /* Add safe area inset for devices with notches/home indicators */
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
  }
</style>
